import type { CatalogoRef, DateOnlyString, Paginated } from '../common';
import type { ClientBalance, InvoiceType } from '../cobros';

export interface ClienteResponse {
  id: number;
  nombre: string | null;
  /** Date-only (fecha_nacimiento DATE column) — see DateOnlyString contract. */
  fechaNacimiento: DateOnlyString | null;
  identificacion: string | null;
  telefonoPrincipal: string | null;
  telefonoReferencia: string | null;
  email: string | null;
  ip: string | null;
  nit: string | null;
  ubicacion: string | null;
  comentarios: string | null;
  /** Date-only (fecha_cobro DATE column) — see DateOnlyString contract. */
  fechaCobro: DateOnlyString | null;
  diaCobro: number | null;
  /** Real instant (fecha_alta timestamptz column) — NOT date-only. */
  fechaAlta: Date | null;
  fechaCreacion: Date;
  fechaModificacion: Date;
  usuarioModifica: string | null;
  estadoCliente: CatalogoRef | null;
  tipoCliente: CatalogoRef | null;
  /**
   * Two independent, non-negative balance values (spec: client-balance-reporting).
   * `deudaPendiente` = sum of open non-annulled invoice remainders.
   * `saldoAFavor` = sum of available credit lots. NEVER netted together.
   */
  deudaPendiente?: string;
  saldoAFavor?: string;
}

/**
 * Monetary/aggregate summary for the 360 client view.
 * Spec migration: `saldo`/`credito` are read-only compatibility aliases.
 * New code MUST consume `deudaPendiente` and `saldoAFavor` separately.
 */
export interface ClienteResumenResponse {
  /** Deprecated compatibility alias for `deudaPendiente`. */
  saldo: number;
  /** Two-value contract (preferred). Non-negative, never netted. */
  deudaPendiente: string;
  /** Two-value contract (preferred). Non-negative. */
  saldoAFavor: string;
  totalFacturado: number;
  totalPagado: number;
  serviciosActivos: number;
  /**
   * Monthly amount to charge for the active services: sum of positive
   * plan prices across services whose state is ACTIVO or ACTIVO_AL_CORTE
   * (same rule as the services tab's precioTotalActivos). Text NUMERIC;
   * consumers parse with Number().
   */
  serviciosActivosMontoMensual: string;
  /** Deprecated compatibility alias for `saldoAFavor`. */
  credito: number;
}

/**
 * billing-domain-rules (Phase 6.2): per-invoice balance row derived from
 * `view_invoice_balance` for the cliente 360 detail. The sum of every
 * non-annulled `saldoPendiente` MUST equal the parent's `deudaPendiente`.
 * All amounts are text (NUMERIC); consumers parse with `Number()`.
 */
export interface ClienteFacturaBalance {
  id: number;
  idCliente: number;
  tipoFactura: InvoiceType;
  fechaInicio: string | null;
  fechaFin: string | null;
  concepto: string | null;
  gross: string;
  ajustesAplicados: string;
  montoAplicado: string;
  saldoPendiente: string;
  anulada: boolean;
  fechaAnulacion: string | null;
}

/** Stable movement categories exposed by the Cliente 360 subsidiary ledger. */
export const ESTADO_CUENTA_MOVEMENT_TYPE = {
  CARGO: 'CARGO',
  PAGO: 'PAGO',
  PREPAGO: 'PREPAGO',
  CREDITO_GENERADO: 'CREDITO_GENERADO',
  CREDITO_APLICADO: 'CREDITO_APLICADO',
  AJUSTE: 'AJUSTE',
  ANULACION: 'ANULACION',
  REVERSO: 'REVERSO',
  RECONCILIACION: 'RECONCILIACION',
} as const;

export type EstadoCuentaMovementType =
  (typeof ESTADO_CUENTA_MOVEMENT_TYPE)[keyof typeof ESTADO_CUENTA_MOVEMENT_TYPE];

/** Certainty marker for reconstructed historical rows. */
export const ESTADO_CUENTA_CERTAINTY = {
  EXACTA: 'EXACTA',
  LEGACY_INCIERTO: 'LEGACY_INCIERTO',
} as const;

export type EstadoCuentaCertainty =
  (typeof ESTADO_CUENTA_CERTAINTY)[keyof typeof ESTADO_CUENTA_CERTAINTY];

/** Binary formats supported by the complete Cliente 360 statement export. */
export const ESTADO_CUENTA_EXPORT_FORMAT = {
  PDF: 'pdf',
  XLSX: 'xlsx',
} as const;

export type EstadoCuentaExportFormat =
  (typeof ESTADO_CUENTA_EXPORT_FORMAT)[keyof typeof ESTADO_CUENTA_EXPORT_FORMAT];

export const ESTADO_CUENTA_WARNING_CODE = {
  LEGACY_INCIERTO: 'LEGACY_INCIERTO',
  RECONCILIACION_CANONICA: 'RECONCILIACION_CANONICA',
} as const;

export type EstadoCuentaWarningCode =
  (typeof ESTADO_CUENTA_WARNING_CODE)[keyof typeof ESTADO_CUENTA_WARNING_CODE];

/** An application is child detail of a statement movement, never a movement itself. */
export interface EstadoCuentaApplication {
  id: number;
  idFactura: number;
  idPago: number | null;
  idCredito: number | null;
  montoAplicado: string;
  estado: string;
}

/** Optional source detail shown alongside a movement. */
export interface EstadoCuentaMovementDetail {
  tipoFactura?: string | null;
  concepto?: string | null;
  idFactura?: number | null;
  idPago?: number | null;
  idCredito?: number | null;
  idPaquete?: number | null;
  idExcepcion?: number | null;
  periodoInicio?: string | null;
  periodoFin?: string | null;
  motivo?: string | null;
  montoDisponible?: string | null;
  ajustesAplicados?: string | null;
  montoAplicado?: string | null;
  saldoPendiente?: string | null;
}

/** One chronological economic movement with independent balance effects. */
export interface EstadoCuentaMovement {
  id: string;
  fechaEfectiva: string;
  fechaRegistro: string;
  tipo: EstadoCuentaMovementType;
  origen: string;
  monto: string;
  deudaDelta: string;
  saldoAFavorDelta: string;
  deudaAntes: string;
  saldoAFavorAntes: string;
  deudaPendiente: string;
  saldoAFavor: string;
  referencia: string;
  estado: string;
  descripcion: string;
  certeza: EstadoCuentaCertainty;
  auditEventId: number | null;
  detalle?: EstadoCuentaMovementDetail | null;
  aplicaciones?: EstadoCuentaApplication[];
}

export interface EstadoCuentaWarning {
  code: EstadoCuentaWarningCode;
  count: number;
}

/** Paginated Cliente 360 statement response; balances are never netted. */
export interface EstadoCuentaResponse extends Paginated<EstadoCuentaMovement> {
  balanceActual: ClientBalance;
  balanceInicial: ClientBalance;
  advertencias: EstadoCuentaWarning[];
}

/** Configuration shared by the monthly financial-history projection. */
export const ESTADO_CUENTA_SERIE_MENSUAL = {
  DEFAULT_MONTHS: 12,
  MAX_MONTHS: 24,
  TIMEZONE: 'America/Guatemala',
} as const;

/** One exact calendar month in the Cliente 360 financial-history response. */
export interface EstadoCuentaSerieMensualBucket {
  /** YYYY-MM bucket key. */
  mes: string;
  fechaDesde: string;
  fechaHasta: string;
  facturado: string;
  pagado: string;
  deudaPendiente: string;
  saldoAFavor: string;
}

/** Period metadata for a monthly financial-history response. */
export interface EstadoCuentaSerieMensualPeriod {
  fechaDesde: string;
  fechaHasta: string;
  meses: number;
  zonaHoraria: string;
}

/** Explicit metric semantics carried by the monthly response. */
export const ESTADO_CUENTA_SERIE_MENSUAL_SEMANTICS = {
  FACTURADO: 'EMISION_ACTIVA',
  PAGADO: 'EFECTIVO_RECIBIDO',
} as const;

export interface EstadoCuentaSerieMensualSemantics {
  /** Gross active invoice amount bucketed by cobro_cliente.fecha_creacion. */
  facturado: typeof ESTADO_CUENTA_SERIE_MENSUAL_SEMANTICS.FACTURADO;
  /** Positive VIGENTE payment amount bucketed by pago_cliente.fecha_pago. */
  pagado: typeof ESTADO_CUENTA_SERIE_MENSUAL_SEMANTICS.PAGADO;
}

/** Non-paginated monthly financial-history response for Cliente 360. */
export interface EstadoCuentaSerieMensualResponse {
  data: EstadoCuentaSerieMensualBucket[];
  periodo: EstadoCuentaSerieMensualPeriod;
  semantica: EstadoCuentaSerieMensualSemantics;
  balanceInicial: ClientBalance;
  balanceFinal: ClientBalance;
  advertencias: EstadoCuentaWarning[];
}
